import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext, API } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { MultiSelect } from "./ui/MultiSelect";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { ArrowLeft, FileText, Building2, AlertCircle, Clock, CheckCircle, TrendingUp, Download, Eye, Lock, Edit } from "lucide-react";
import { toast } from "sonner";
import jsPDF from "jspdf";
import 'jspdf-autotable';

export default function DefectsReporting() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const isViewOnly = user?.role === "view_only";
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSite, setSelectedSite] = useState(null);
  const [siteDefects, setSiteDefects] = useState([]);
  const [loadingDefects, setLoadingDefects] = useState(false);
  const [contractors, setContractors] = useState([]);
  const [filteredDefects, setFilteredDefects] = useState([]);
  const [selectedContractors, setSelectedContractors] = useState([]);
  const [selectedDwellings, setSelectedDwellings] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [reassigningId, setReassigningId] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      const loadedContractors = await loadContractors();
      await loadSitesWithDefects(loadedContractors);
    };
    loadData();
  }, []);

  const loadContractors = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/companies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        // Keep ALL companies so any assigned contractor/subcontractor name always
        // resolves (never "Unknown"), and a defect's current assignment always
        // appears in its own row dropdown — regardless of company_type or site link.
        setContractors(data);
        return data;
      }
      return [];
    } catch (error) {
      console.error("Error loading contractors:", error);
      return [];
    }
  };

  const loadSitesWithDefects = async (contractorsList = contractors) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      
      // Load sites
      const sitesResponse = await fetch(`${API}/sites`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (sitesResponse.ok) {
        const sitesData = await sitesResponse.json();
        
        // For each site, load its dwellings and defects to calculate stats and get contractor/dwelling info
        const sitesWithStats = await Promise.all(
          sitesData.map(async (site) => {
            try {
              // Load dwellings
              const dwellingsResponse = await fetch(`${API}/dwellings?site_id=${site.id}`, {
                headers: { Authorization: `Bearer ${token}` }
              });
              
              if (dwellingsResponse.ok) {
                const dwellings = await dwellingsResponse.json();
                
                const totalDefects = dwellings.reduce((sum, d) => sum + d.total_defects, 0);
                const completedDefects = dwellings.reduce((sum, d) => sum + d.completed_defects, 0);
                const inProgressDefects = dwellings.reduce((sum, d) => sum + d.in_progress_defects, 0);
                const newDefects = totalDefects - completedDefects - inProgressDefects;
                
                // Load defects to get contractor and dwelling information
                let contractorNames = [];
                let dwellingNumbers = [];
                let closedOutDefects = 0;
                
                if (totalDefects > 0) {
                  try {
                    const defectsResponse = await fetch(`${API}/defects?site_id=${site.id}`, {
                      headers: { Authorization: `Bearer ${token}` }
                    });
                    
                    if (defectsResponse.ok) {
                      const defects = await defectsResponse.json();
                      
                      // Count closed-out defects (subset of completed)
                      closedOutDefects = defects.filter(d => d.closed_out).length;
                      
                      // Get unique contractor IDs
                      const contractorIds = [...new Set(defects.map(d => d.assigned_to).filter(Boolean))];
                      
                      // Get contractor names
                      contractorNames = contractorIds.map(id => {
                        const contractor = contractorsList.find(c => c.id === id);
                        return contractor?.name || "Unknown";
                      }).filter(Boolean);
                      
                      // Check for unassigned defects
                      const hasUnassigned = defects.some(d => !d.assigned_to);
                      if (hasUnassigned) {
                        contractorNames.push("Unassigned");
                      }
                      
                      // Get unique dwelling numbers (sorted naturally)
                      dwellingNumbers = [...new Set(defects.map(d => d.dwelling_number).filter(Boolean))]
                        .sort((a, b) => {
                          // Natural sort for dwelling numbers
                          return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
                        });
                    }
                  } catch (error) {
                    console.error(`Error loading defects for site ${site.id}:`, error);
                  }
                }
                
                return {
                  ...site,
                  totalDefects,
                  newDefects,
                  inProgressDefects,
                  completedDefects,
                  closedOutDefects,
                  dwellingsCount: dwellings.length,
                  contractorNames,
                  dwellingNumbers
                };
              }
              
              return {
                ...site,
                totalDefects: 0,
                newDefects: 0,
                inProgressDefects: 0,
                completedDefects: 0,
                closedOutDefects: 0,
                dwellingsCount: 0,
                contractorNames: [],
                dwellingNumbers: []
              };
            } catch (error) {
              console.error(`Error loading dwellings for site ${site.id}:`, error);
              return {
                ...site,
                totalDefects: 0,
                newDefects: 0,
                inProgressDefects: 0,
                completedDefects: 0,
                closedOutDefects: 0,
                dwellingsCount: 0,
                contractorNames: [],
                dwellingNumbers: []
              };
            }
          })
        );
        
        // Filter out sites with no defects and sort by total defects descending
        const sitesWithDefects = sitesWithStats
          .filter(site => site.totalDefects > 0)
          .sort((a, b) => b.totalDefects - a.totalDefects);
        
        setSites(sitesWithDefects);
      }
    } catch (error) {
      console.error("Error loading sites:", error);
      toast.error("Failed to load sites data");
    } finally {
      setLoading(false);
    }
  };

  const handleSiteClick = async (site) => {
    const newSelectedSite = site.id === selectedSite ? null : site.id;
    setSelectedSite(newSelectedSite);
    
    if (newSelectedSite) {
      // Reset filters
      setSelectedContractors([]);
      setSelectedDwellings([]);
      setSelectedStatuses([]);
      
      // Load defects for this site
      setLoadingDefects(true);
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(`${API}/defects?site_id=${site.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.ok) {
          const defects = await response.json();
          setSiteDefects(defects);
          setFilteredDefects(defects);
        }
      } catch (error) {
        console.error("Error loading defects:", error);
        toast.error("Failed to load defects");
      } finally {
        setLoadingDefects(false);
      }
    } else {
      setSiteDefects([]);
      setFilteredDefects([]);
      setSelectedContractors([]);
      setSelectedDwellings([]);
      setSelectedStatuses([]);
    }
  };

  // Filter defects when contractor, dwelling, or status selection changes
  useEffect(() => {
    if (siteDefects.length === 0) {
      setFilteredDefects([]);
      return;
    }

    let filtered = [...siteDefects];

    // Filter by contractors
    if (selectedContractors.length > 0) {
      filtered = filtered.filter(defect => {
        // Include unassigned if "unassigned" is selected
        if (selectedContractors.includes("unassigned") && !defect.assigned_to) {
          return true;
        }
        // Check if defect has the selected contractor
        return defect.assigned_to && selectedContractors.includes(defect.assigned_to);
      });
    }

    // Filter by dwellings
    if (selectedDwellings.length > 0) {
      filtered = filtered.filter(defect => 
        selectedDwellings.includes(defect.dwelling_id)
      );
    }

    // Filter by statuses (uses effective status: closed-out shows as "Closed Out")
    if (selectedStatuses.length > 0) {
      filtered = filtered.filter(defect =>
        selectedStatuses.includes(getEffectiveStatus(defect))
      );
    }

    setFilteredDefects(filtered);
  }, [selectedContractors, selectedDwellings, selectedStatuses, siteDefects]);

  // Get unique dwellings from current site defects
  const getUniqueDwellings = () => {
    const dwellingMap = new Map();
    siteDefects.forEach(defect => {
      if (!dwellingMap.has(defect.dwelling_id)) {
        dwellingMap.set(defect.dwelling_id, defect.dwelling_number);
      }
    });
    return Array.from(dwellingMap.entries()).map(([id, name]) => ({
      value: id,
      label: name
    }));
  };

  // Get unique contractors from current site defects
  const getUniqueContractors = () => {
    const contractorSet = new Set();
    let hasUnassigned = false;

    siteDefects.forEach(defect => {
      if (!defect.assigned_to) {
        hasUnassigned = true;
      } else {
        contractorSet.add(defect.assigned_to);
      }
    });

    const contractorOptions = Array.from(contractorSet)
      .map(id => {
        const contractor = contractors.find(c => c.id === id);
        return contractor ? { value: id, label: contractor.name } : null;
      })
      .filter(Boolean);

    if (hasUnassigned) {
      contractorOptions.push({ value: "unassigned", label: "Unassigned" });
    }

    return contractorOptions;
  };

  const getUniqueStatuses = () => {
    const statusSet = new Set();
    
    siteDefects.forEach(defect => {
      const eff = getEffectiveStatus(defect);
      if (eff) {
        statusSet.add(eff);
      }
    });
    
    return Array.from(statusSet).map(status => ({
      value: status,
      label: statusLabelMap[status] || status
    }));
  };

  const handleViewDetails = (siteId) => {
    navigate(`/defects/${siteId}/details`);
  };

  const getContractorNames = (contractorId) => {
    if (!contractorId) return "Unassigned";
    const contractor = contractors.find(c => c.id === contractorId);
    return contractor?.name || "Unknown";
  };

  // Contractors + subcontractors for the selected site — companies linked to the
  // site via `site_ids` OR already assigned to a defect on this site. Also always
  // includes the given defect's current assignment so its dropdown is never blank.
  const getAssignableCompanies = (defect) => {
    const assignedIds = new Set(siteDefects.map(d => d.assigned_to).filter(Boolean));
    const list = contractors.filter((c) => {
      const isType =
        c.company_type === "contractor" || c.company_type === "subcontractor";
      const linkedToSite =
        Array.isArray(c.site_ids) && c.site_ids.includes(selectedSite);
      return isType && (linkedToSite || assignedIds.has(c.id));
    });
    // Guarantee the current assignment is selectable even if it doesn't match the filter
    if (defect?.assigned_to && !list.some(c => c.id === defect.assigned_to)) {
      const current = contractors.find(c => c.id === defect.assigned_to);
      if (current) list.push(current);
    }
    return list.sort((a, b) => a.name.localeCompare(b.name));
  };

  const handleReassign = async (defect, newAssignedTo) => {
    const mapped = newAssignedTo === "__unassigned__" ? null : newAssignedTo;
    if (mapped === (defect.assigned_to || null)) return;
    setReassigningId(defect.id);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/defects/${defect.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ assigned_to: mapped }),
      });
      if (res.ok) {
        const updated = await res.json();
        setSiteDefects(prev =>
          prev.map(d =>
            d.id === defect.id ? { ...d, assigned_to: updated.assigned_to } : d
          )
        );
        toast.success(`Reassigned to ${getContractorNames(updated.assigned_to)}`);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Failed to reassign contractor");
      }
    } catch (error) {
      console.error("Error reassigning contractor:", error);
      toast.error("Failed to reassign contractor");
    } finally {
      setReassigningId(null);
    }
  };

  // A closed-out defect is shown as its final state "Closed Out" (completed is implied)
  const getEffectiveStatus = (defect) =>
    defect.closed_out ? "closed_out" : defect.status;

  const statusLabelMap = {
    new: "New",
    in_progress: "In Progress",
    completed: "Completed",
    closed_out: "Closed Out"
  };

  const getStatusBadge = (status) => {
    const styles = {
      new: "bg-blue-100 text-blue-700",
      in_progress: "bg-amber-100 text-amber-700",
      completed: "bg-green-100 text-green-700",
      closed_out: "bg-slate-800 text-white"
    };
    return styles[status] || "bg-slate-100 text-slate-700";
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      low: "bg-slate-100 text-slate-700",
      medium: "bg-orange-100 text-orange-700",
      high: "bg-red-100 text-red-700"
    };
    return styles[priority] || "bg-slate-100 text-slate-700";
  };

  const generatePdfReport = async () => {
    setGeneratingPdf(true);
    
    try {
      console.log("Starting PDF generation...");
      console.log("Filtered defects count:", filteredDefects.length);
      
      if (filteredDefects.length === 0) {
        toast.error("No defects to generate report");
        setGeneratingPdf(false);
        return;
      }
      
      const doc = new jsPDF();
      const siteName = sites.find(s => s.id === selectedSite)?.name || "Site";
      
      console.log("Creating PDF for site:", siteName);
      
      // Title Page
      doc.setFontSize(20);
      doc.text("Defects Report", 14, 20);
      
      doc.setFontSize(14);
      doc.text(siteName, 14, 28);
      
      doc.setFontSize(10);
      doc.text(`Generated: ${new Date().toLocaleDateString("en-AU")} ${new Date().toLocaleTimeString("en-AU")}`, 14, 34);
      
      let yPos = 44;
      
      // Filter information section
      if (selectedContractors.length > 0 || selectedDwellings.length > 0 || selectedStatuses.length > 0) {
        doc.setFontSize(12);
        doc.setFont(undefined, "bold");
        doc.text("Filters Applied:", 14, yPos);
        yPos += 7;
        
        doc.setFontSize(10);
        doc.setFont(undefined, "normal");
        
        if (selectedContractors.length > 0) {
          const contractorNames = selectedContractors.map(id => {
            if (id === "unassigned") return "Unassigned";
            const contractor = contractors.find(c => c.id === id);
            return contractor?.name || "Unknown";
          });
          
          doc.setFont(undefined, "bold");
          doc.text("Contractors:", 14, yPos);
          doc.setFont(undefined, "normal");
          yPos += 5;
          
          // Wrap long contractor list
          const contractorText = contractorNames.join(", ");
          const wrappedContractors = doc.splitTextToSize(contractorText, 180);
          doc.text(wrappedContractors, 20, yPos);
          yPos += wrappedContractors.length * 5 + 3;
        }
        
        if (selectedDwellings.length > 0) {
          const dwellingNames = selectedDwellings.map(id => {
            const defect = siteDefects.find(d => d.dwelling_id === id);
            return defect?.dwelling_number || "Unknown";
          });
          
          doc.setFont(undefined, "bold");
          doc.text("Dwellings:", 14, yPos);
          doc.setFont(undefined, "normal");
          yPos += 5;
          
          // Wrap long dwelling list
          const dwellingText = dwellingNames.join(", ");
          const wrappedDwellings = doc.splitTextToSize(dwellingText, 180);
          doc.text(wrappedDwellings, 20, yPos);
          yPos += wrappedDwellings.length * 5 + 3;
        }
        
        if (selectedStatuses.length > 0) {
          const statusNames = selectedStatuses.map(status => statusLabelMap[status] || status);
          
          doc.setFont(undefined, "bold");
          doc.text("Statuses:", 14, yPos);
          doc.setFont(undefined, "normal");
          yPos += 5;
          
          const statusText = statusNames.join(", ");
          const wrappedStatuses = doc.splitTextToSize(statusText, 180);
          doc.text(wrappedStatuses, 20, yPos);
          yPos += wrappedStatuses.length * 5 + 3;
        }
        
        yPos += 5;
        
        // Add separator line
        doc.setDrawColor(200, 200, 200);
        doc.line(14, yPos, 196, yPos);
        yPos += 8;
      }
      
      // Summary stats
      doc.setFontSize(12);
      doc.setFont(undefined, "bold");
      doc.text("Summary", 14, yPos);
      yPos += 8;
      
      doc.setFontSize(10);
      doc.setFont(undefined, "normal");
      const newCount = filteredDefects.filter(d => getEffectiveStatus(d) === "new").length;
      const inProgressCount = filteredDefects.filter(d => getEffectiveStatus(d) === "in_progress").length;
      const completedCount = filteredDefects.filter(d => getEffectiveStatus(d) === "completed").length;
      const closedOutCount = filteredDefects.filter(d => d.closed_out).length;
      
      doc.text(`Total Defects: ${filteredDefects.length}`, 14, yPos);
      doc.text(`New: ${newCount}`, 70, yPos);
      doc.text(`In Progress: ${inProgressCount}`, 120, yPos);
      yPos += 6;
      doc.text(`Completed: ${completedCount}`, 14, yPos);
      doc.text(`Closed Out: ${closedOutCount}`, 70, yPos);
      
      // Additional summary info
      yPos += 7;
      const uniqueDwellings = new Set(filteredDefects.map(d => d.dwelling_id)).size;
      const uniqueContractors = new Set(filteredDefects.map(d => d.assigned_to).filter(Boolean)).size;
      const unassignedCount = filteredDefects.filter(d => !d.assigned_to).length;
      
      doc.text(`Dwellings Affected: ${uniqueDwellings}`, 14, yPos);
      doc.text(`Contractors Involved: ${uniqueContractors}`, 90, yPos);
      yPos += 5;
      doc.text(`Unassigned Defects: ${unassignedCount}`, 14, yPos);
      
      // Group defects by contractor
      const defectsByContractor = {};
      
      filteredDefects.forEach(defect => {
        const contractorName = getContractorNames(defect.assigned_to);
        if (!defectsByContractor[contractorName]) {
          defectsByContractor[contractorName] = [];
        }
        defectsByContractor[contractorName].push(defect);
      });
      
      // Sort contractors alphabetically
      const sortedContractors = Object.keys(defectsByContractor).sort();
      
      console.log("Contractors in report:", sortedContractors);
      
      // Generate report for each contractor
      sortedContractors.forEach((contractorName, contractorIndex) => {
        const contractorDefects = defectsByContractor[contractorName];
        
        // New page for each contractor
        if (contractorIndex > 0) {
          doc.addPage();
        } else {
          doc.addPage();
        }
        
        yPos = 20;
        
        // Contractor header
        doc.setFontSize(16);
        doc.setFont(undefined, "bold");
        doc.text(`Contractor: ${contractorName}`, 14, yPos);
        yPos += 8;
        
        doc.setFontSize(10);
        doc.setFont(undefined, "normal");
        doc.text(`Total Defects: ${contractorDefects.length}`, 14, yPos);
        yPos += 10;
        
        // Defects for this contractor
        doc.setFontSize(12);
        doc.setFont(undefined, "bold");
        doc.text("Defects:", 14, yPos);
        yPos += 8;
        
        doc.setFontSize(9);
        doc.setFont(undefined, "normal");
        
        for (let i = 0; i < contractorDefects.length; i++) {
          const defect = contractorDefects[i];
          
          // Check if we need a new page
          if (yPos > 260) {
            doc.addPage();
            yPos = 20;
            
            // Repeat contractor header on new page
            doc.setFontSize(12);
            doc.setFont(undefined, "bold");
            doc.text(`Contractor: ${contractorName} (continued)`, 14, yPos);
            yPos += 10;
            doc.setFontSize(9);
            doc.setFont(undefined, "normal");
          }
          
          // Defect number
          doc.setFont(undefined, "bold");
          doc.text(`Defect #${defect.number || 'N/A'}`, 14, yPos);
          doc.setFont(undefined, "normal");
          yPos += 5;
          
          // Dwelling
          doc.text(`Dwelling: ${siteName} - ${defect.dwelling_number || 'N/A'}`, 20, yPos);
          yPos += 5;
          
          // Area and Element
          doc.text(`Area: ${defect.area || 'N/A'}`, 20, yPos);
          doc.text(`Element: ${defect.element || 'N/A'}`, 90, yPos);
          yPos += 5;
          
          // Issue
          doc.text(`Issue: ${defect.issue || 'N/A'}`, 20, yPos);
          yPos += 5;
          
          // Priority, Status, Due Date
          const effStatus = getEffectiveStatus(defect);
          const statusText =
            effStatus === "closed_out"
              ? "CLOSED OUT"
              : (effStatus || "new").replace("_", " ").toUpperCase();
          doc.text(`Priority: ${(defect.priority || 'medium').toUpperCase()}`, 20, yPos);
          doc.text(`Status: ${statusText}`, 70, yPos);
          doc.text(`Due: ${defect.due_date || 'Not set'}`, 130, yPos);
          yPos += 5;
          
          // Close-out audit trail
          if (defect.closed_out) {
            const closedBy = defect.closed_out_by_name || "Unknown";
            const closedOn = defect.closed_out_at
              ? new Date(defect.closed_out_at).toLocaleDateString("en-AU")
              : "";
            doc.text(
              `Closed Out by: ${closedBy}${closedOn ? ` on ${closedOn}` : ""}`,
              20,
              yPos
            );
            yPos += 5;
          }
          
          // Details if available
          if (defect.details) {
            doc.setFontSize(8);
            doc.text("Details:", 20, yPos);
            yPos += 4;
            const details = doc.splitTextToSize(defect.details, 170);
            doc.text(details, 20, yPos);
            yPos += details.length * 4 + 2;
            doc.setFontSize(9);
          }
          
          // Images if available
          if (defect.images && defect.images.length > 0) {
            // Check if we have space for at least one image
            if (yPos > 210) {
              doc.addPage();
              yPos = 20;
              doc.setFontSize(10);
              doc.setFont(undefined, "bold");
              doc.text(`Defect #${defect.number} - Photos`, 14, yPos);
              yPos += 8;
            }
            
            doc.setFontSize(8);
            doc.setFont(undefined, "bold");
            doc.text(`Photos (${defect.images.length}):`, 20, yPos);
            doc.setFont(undefined, "normal");
            yPos += 5;
            
            let imgX = 20;
            let imagesInRow = 0;
            
            for (let j = 0; j < Math.min(defect.images.length, 3); j++) {
              try {
                // Check if we need a new page for images
                if (yPos > 220) {
                  doc.addPage();
                  yPos = 20;
                  imgX = 20;
                  imagesInRow = 0;
                }
                
                const img = defect.images[j];
                doc.addImage(img, 'PNG', imgX, yPos, 40, 40);
                imgX += 45;
                imagesInRow++;
                
                // Move to next row after 4 images
                if (imagesInRow >= 4) {
                  yPos += 45;
                  imgX = 20;
                  imagesInRow = 0;
                }
              } catch (error) {
                console.error("Error adding image to PDF:", error);
              }
            }
            
            // Move yPos past the images
            if (imagesInRow > 0) {
              yPos += 45;
            }
            
            doc.setFontSize(9);
          }
          
          yPos += 3;
          
          // Separator line
          if (yPos < 280) {
            doc.setDrawColor(220, 220, 220);
            doc.line(14, yPos, 196, yPos);
            yPos += 5;
          }
        }
      });
      
      console.log("PDF generated successfully, adding page numbers...");
      
      // Add page numbers to all pages
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(9);
        doc.setFont(undefined, "normal");
        doc.setTextColor(128, 128, 128);
        doc.text(
          `Page ${i} of ${pageCount}`,
          doc.internal.pageSize.width / 2,
          doc.internal.pageSize.height - 10,
          { align: 'center' }
        );
      }
      
      console.log("Page numbers added, creating blob...");
      
      // Create blob URL for preview
      const pdfBlob = doc.output('blob');
      const url = URL.createObjectURL(pdfBlob);
      
      console.log("PDF blob URL created:", url);
      
      setPdfUrl(url);
      setShowPdfPreview(true);
      toast.success("PDF generated successfully");
      
    } catch (error) {
      console.error("Error generating PDF:", error);
      console.error("Error stack:", error.stack);
      toast.error(`Failed to generate PDF: ${error.message}`);
    } finally {
      setGeneratingPdf(false);
    }
  };

  const handleDownloadPdf = () => {
    if (pdfUrl) {
      const siteName = sites.find(s => s.id === selectedSite)?.name || "Site";
      const fileName = `Defects_Report_${siteName.replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
      
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = fileName;
      link.click();
      
      toast.success("PDF downloaded successfully");
    }
  };

  const handleClosePdfPreview = () => {
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
    setShowPdfPreview(false);
  };

  if (loading) {
    return (
      <div className="p-4 sm:p-8 max-w-7xl mx-auto">
        <div className="mb-6">
          <Button variant="outline" onClick={() => navigate("/defects")}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        </div>
        <div className="text-center py-12">
          <p className="text-slate-600">Loading defects data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button variant="outline" onClick={() => navigate("/defects")}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="heading-primary text-2xl sm:text-3xl">Defects Reporting</h1>
            <p className="text-slate-600">Overview of defects across all sites</p>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      {sites.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <Card className="dashboard-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 uppercase mb-1">Sites with Defects</p>
                  <p className="text-2xl font-bold text-slate-900">{sites.length}</p>
                </div>
                <Building2 className="w-8 h-8 text-slate-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="dashboard-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 uppercase mb-1">Total Defects</p>
                  <p className="text-2xl font-bold text-slate-900">
                    {sites.reduce((sum, s) => sum + s.totalDefects, 0)}
                  </p>
                </div>
                <FileText className="w-8 h-8 text-slate-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="dashboard-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 uppercase mb-1">In Progress</p>
                  <p className="text-2xl font-bold text-amber-600">
                    {sites.reduce((sum, s) => sum + s.inProgressDefects, 0)}
                  </p>
                </div>
                <Clock className="w-8 h-8 text-amber-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="dashboard-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 uppercase mb-1">Completed</p>
                  <p className="text-2xl font-bold text-green-600">
                    {sites.reduce((sum, s) => sum + (s.completedDefects - (s.closedOutDefects || 0)), 0)}
                  </p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="dashboard-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 uppercase mb-1">Closed Out</p>
                  <p className="text-2xl font-bold text-slate-800" data-testid="reporting-closedout-total">
                    {sites.reduce((sum, s) => sum + (s.closedOutDefects || 0), 0)}
                  </p>
                </div>
                <Lock className="w-8 h-8 text-slate-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Sites Table */}
      <Card className="dashboard-card">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Building2 className="w-5 h-5" />
            <span>Sites Overview</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {sites.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 mx-auto text-slate-300 mb-4" />
              <p className="text-lg text-slate-600 mb-2">No Defects Found</p>
              <p className="text-sm text-slate-500">
                There are currently no defects recorded across any sites.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-100 border-b-2 border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Site</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Contractors</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Dwellings with Defects</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700">Total Defects</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700">New</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700">In Progress</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700">Completed</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700">Closed Out</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700">Completion %</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {sites.map((site) => {
                    const completionPercent = site.totalDefects > 0 
                      ? Math.round((site.completedDefects / site.totalDefects) * 100)
                      : 0;
                    const isSelected = selectedSite === site.id;
                    
                    return (
                      <tr
                        key={site.id}
                        className={`cursor-pointer transition-all border-l-4 ${
                          isSelected 
                            ? 'bg-blue-50 border-l-blue-500' 
                            : 'hover:bg-slate-50 border-l-transparent hover:border-l-blue-300'
                        }`}
                        onClick={() => handleSiteClick(site)}
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center space-x-2">
                            <Building2 className="w-4 h-4 text-blue-500" />
                            <div>
                              <p className="text-sm font-medium text-slate-900">{site.name}</p>
                              {site.address && (
                                <p className="text-xs text-slate-500">{site.address}</p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {site.contractorNames && site.contractorNames.length > 0 ? (
                              site.contractorNames.map((name, idx) => (
                                <Badge 
                                  key={idx} 
                                  variant="secondary" 
                                  className="text-xs bg-blue-100 text-blue-700"
                                >
                                  {name}
                                </Badge>
                              ))
                            ) : (
                              <span className="text-xs text-slate-400 italic">None</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm text-slate-600">
                            {site.dwellingNumbers && site.dwellingNumbers.length > 0 ? (
                              <div className="max-w-xs">
                                {site.dwellingNumbers.length <= 3 ? (
                                  site.dwellingNumbers.join(", ")
                                ) : (
                                  <>
                                    {site.dwellingNumbers.slice(0, 3).join(", ")}
                                    <span className="text-slate-400"> +{site.dwellingNumbers.length - 3} more</span>
                                  </>
                                )}
                              </div>
                            ) : (
                              <span className="text-xs text-slate-400 italic">None</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <Badge variant="outline" className="font-semibold">
                            {site.totalDefects}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <AlertCircle className="w-3 h-3 text-blue-500" />
                            <span className="text-sm font-semibold text-blue-600">
                              {site.newDefects}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Clock className="w-3 h-3 text-amber-500" />
                            <span className="text-sm font-semibold text-amber-600">
                              {site.inProgressDefects}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <CheckCircle className="w-3 h-3 text-green-500" />
                            <span className="text-sm font-semibold text-green-600">
                              {site.completedDefects - (site.closedOutDefects || 0)}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Lock className="w-3 h-3 text-slate-500" />
                            <span className="text-sm font-semibold text-slate-800">
                              {site.closedOutDefects || 0}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <TrendingUp className="w-3 h-3 text-slate-400" />
                            <span className="text-sm font-semibold text-slate-700">
                              {completionPercent}%
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Defects Detail Table - Shows when site is selected */}
      {selectedSite && (
        <Card className="dashboard-card mt-6">
          <CardHeader>
            <div className="flex flex-col space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <FileText className="w-5 h-5" />
                  <span className="font-semibold">Defects for {sites.find(s => s.id === selectedSite)?.name}</span>
                </div>
                <Button
                  size="sm"
                  onClick={generatePdfReport}
                  disabled={generatingPdf || filteredDefects.length === 0}
                  className="btn-construction"
                >
                  {generatingPdf ? (
                    <>Generating...</>
                  ) : (
                    <>
                      <FileText className="w-4 h-4 mr-2" />
                      Create PDF
                    </>
                  )}
                </Button>
              </div>

              {/* Filters */}
              {!loadingDefects && siteDefects.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t">
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-2 block">
                      Filter by Contractor
                    </label>
                    <MultiSelect
                      options={getUniqueContractors()}
                      selected={selectedContractors}
                      onChange={setSelectedContractors}
                      placeholder="All contractors"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-2 block">
                      Filter by Dwelling
                    </label>
                    <MultiSelect
                      options={getUniqueDwellings()}
                      selected={selectedDwellings}
                      onChange={setSelectedDwellings}
                      placeholder="All dwellings"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-2 block">
                      Filter by Status
                    </label>
                    <MultiSelect
                      options={getUniqueStatuses()}
                      selected={selectedStatuses}
                      onChange={setSelectedStatuses}
                      placeholder="All statuses"
                    />
                  </div>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {loadingDefects ? (
              <div className="text-center py-8">
                <p className="text-slate-600">Loading defects...</p>
              </div>
            ) : siteDefects.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-slate-600">No defects found for this site</p>
              </div>
            ) : filteredDefects.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-slate-600">No defects match the selected filters</p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-4"
                  onClick={() => {
                    setSelectedContractors([]);
                    setSelectedDwellings([]);
                    setSelectedStatuses([]);
                  }}
                >
                  Clear Filters
                </Button>
              </div>
            ) : (
              <div>
                <div className="mb-3 text-sm text-slate-600">
                  Showing {filteredDefects.length} of {siteDefects.length} defects
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-100 border-b-2 border-slate-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Contractor</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Dwelling</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Area</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Element</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Issue</th>
                        <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700">Priority</th>
                        <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700">Status</th>
                        <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700">Due Date</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Defect #</th>
                        {!isViewOnly && (
                          <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700">Actions</th>
                        )}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {filteredDefects.map((defect) => (
                        <tr key={defect.id} className="hover:bg-slate-50 transition-colors">
                          <td className="px-4 py-3">
                            {isViewOnly || defect.closed_out ? (
                              <span
                                className="text-sm text-slate-700"
                                data-testid={`reporting-contractor-${defect.id}`}
                              >
                                {getContractorNames(defect.assigned_to)}
                              </span>
                            ) : (
                              <Select
                                value={defect.assigned_to || "__unassigned__"}
                                onValueChange={(v) => handleReassign(defect, v)}
                                disabled={reassigningId === defect.id}
                              >
                                <SelectTrigger
                                  className="h-8 text-sm w-44"
                                  data-testid={`reporting-contractor-select-${defect.id}`}
                                >
                                  <SelectValue placeholder="Unassigned" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="__unassigned__">
                                    Unassigned
                                  </SelectItem>
                                  {getAssignableCompanies(defect).map((c) => (
                                    <SelectItem key={c.id} value={c.id}>
                                      {c.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm text-slate-700">
                              {sites.find(s => s.id === selectedSite)?.name} - {defect.dwelling_number}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm text-slate-700">{defect.area}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm text-slate-700">{defect.element}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm text-slate-700">{defect.issue}</span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <Badge className={`text-xs ${getPriorityBadge(defect.priority)}`}>
                              {defect.priority}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <Badge className={`text-xs ${getStatusBadge(getEffectiveStatus(defect))}`} data-testid={`defect-status-badge-${defect.id}`}>
                              {statusLabelMap[getEffectiveStatus(defect)]}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="text-sm text-slate-600">
                              {defect.due_date || '-'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm font-mono font-medium text-slate-900">
                              {defect.number}
                            </span>
                          </td>
                          {!isViewOnly && (
                            <td className="px-4 py-3 text-center">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  navigate(
                                    `/defects/${selectedSite}/dwelling/${defect.dwelling_id}?edit=${defect.id}`
                                  )
                                }
                                className="text-blue-600 hover:text-blue-700 border-blue-200"
                                data-testid={`reporting-edit-btn-${defect.id}`}
                              >
                                <Edit className="w-4 h-4 mr-1" />
                                Edit
                              </Button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* PDF Preview Dialog */}
      <Dialog open={showPdfPreview} onOpenChange={handleClosePdfPreview}>
        <DialogContent className="max-w-4xl h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>PDF Report Preview</DialogTitle>
            <DialogDescription>
              Preview the generated PDF report before downloading.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-hidden">
            {pdfUrl && (
              <iframe
                src={pdfUrl}
                className="w-full h-full border-0"
                title="PDF Preview"
              />
            )}
          </div>
          <DialogFooter className="flex justify-between items-center">
            <div className="text-sm text-slate-600">
              Preview of {filteredDefects.length} defect{filteredDefects.length !== 1 ? 's' : ''}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleClosePdfPreview}>
                Close
              </Button>
              <Button onClick={handleDownloadPdf} className="btn-construction">
                <Download className="w-4 h-4 mr-2" />
                Download PDF
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
